"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const events_1 = require("events");
const loglevel_1 = __importDefault(require("loglevel"));
const livekit_rtc_1 = require("../proto/livekit_rtc");
const errors_1 = require("./errors");
const events_2 = require("./events");
const PCTransport_1 = __importDefault(require("./PCTransport"));
const Track_1 = require("./track/Track");
const utils_1 = require("./utils");
const lossyDataChannel = '_lossy';
const reliableDataChannel = '_reliable';
const maxReconnectRetries = 5;
class RTCEngine extends events_1.EventEmitter {
    constructor(client, config) {
        super();
        this.iceConnected = false;
        this.isClosed = true;
        this.pendingTrackResolvers = {};
        this.reconnectAttempts = 0;
        this.handleDataMessage = (message) => __awaiter(this, void 0, void 0, function* () {
            // decode
            let buffer;
            if (message.data instanceof ArrayBuffer) {
                buffer = message.data;
            }
            else if (message.data instanceof Blob) {
                buffer = yield message.data.arrayBuffer();
            }
            else {
                loglevel_1.default.error('unsupported data type', message.data);
                return;
            }
            const dp = livekit_rtc_1.DataPacket.decode(new Uint8Array(buffer));
            if (dp.speaker) {
                // dispatch speaker updates
                this.emit(events_2.EngineEvent.SpeakersUpdate, dp.speaker.speakers);
            }
            else if (dp.user) {
                this.emit(events_2.EngineEvent.DataPacketReceived, dp.user, dp.kind);
            }
        });
        // websocket reconnect behavior. if websocket is interrupted, and the PeerConnection
        // continues to work, we can reconnect to websocket to continue the session
        // after a number of retries, we'll close and give up permanently
        this.handleDisconnect = (connection) => {
            if (this.isClosed) {
                return;
            }
            loglevel_1.default.debug(`${connection} disconnected`);
            if (this.reconnectAttempts >= maxReconnectRetries) {
                loglevel_1.default.info('could not connect to signal after', maxReconnectRetries, 'attempts. giving up');
                this.close();
                this.emit(events_2.EngineEvent.Disconnected);
                return;
            }
            const delay = (this.reconnectAttempts * this.reconnectAttempts) * 300;
            setTimeout(() => {
                this.reconnect()
                    .then(() => {
                    this.reconnectAttempts = 0;
                })
                    .catch(this.handleDisconnect);
            }, delay);
        };
        this.emitTrackEvent = (track, stream, receiver) => {
            this.emit(events_2.EngineEvent.MediaTrackAdded, track, stream, receiver);
        };
        this.client = client;
        this.rtcConfig = config || {};
        this.useLegacy = utils_1.useLegacyAPI();
        loglevel_1.default.trace('creating RTCEngine', 'useLegacy', this.useLegacy);
    }
    join(url, token, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            this.url = url;
            this.token = token;
            const joinResponse = yield this.client.join(url, token, this.useLegacy, opts);
            this.isClosed = false;
            if (joinResponse.iceServers && !this.rtcConfig.iceServers) {
                const rtcIceServers = [];
                joinResponse.iceServers.forEach((iceServer) => {
                    const rtcIceServer = {
                        urls: iceServer.urls,
                    };
                    if (iceServer.username)
                        rtcIceServer.username = iceServer.username;
                    if (iceServer.credential) {
                        rtcIceServer.credential = iceServer.credential;
                    }
                    rtcIceServers.push(rtcIceServer);
                });
                this.rtcConfig.iceServers = rtcIceServers;
            }
            // update ICE servers before creating PeerConnection
            if (!this.publisher) {
                const conf = this.rtcConfig;
                if (this.useLegacy) {
                    conf.sdpSemantics = 'plan-b';
                }
                this.publisher = new PCTransport_1.default(this.rtcConfig);
                this.subscriber = new PCTransport_1.default(this.rtcConfig);
                this.configure();
            }
            // create offer
            yield this.negotiate();
            return joinResponse;
        });
    }
    close() {
        this.isClosed = true;
        if (this.publisher) {
            this.publisher.pc.getSenders().forEach((sender) => {
                var _a;
                (_a = this.publisher) === null || _a === void 0 ? void 0 : _a.pc.removeTrack(sender);
            });
            this.publisher.close();
            this.publisher = undefined;
        }
        if (this.subscriber) {
            this.subscriber.close();
            this.subscriber = undefined;
        }
        this.client.close();
    }
    addTrack(cid, name, kind, dimension) {
        if (this.pendingTrackResolvers[cid]) {
            throw new errors_1.TrackInvalidError('a track with the same ID has already been published');
        }
        return new Promise((resolve) => {
            this.pendingTrackResolvers[cid] = resolve;
            this.client.sendAddTrack(cid, name, Track_1.Track.kindToProto(kind), dimension);
        });
    }
    updateMuteStatus(trackSid, muted) {
        this.client.sendMuteTrack(trackSid, muted);
    }
    configure() {
        if (!this.publisher || !this.subscriber) {
            return;
        }
        this.publisher.pc.onicecandidate = (ev) => {
            if (!ev.candidate)
                return;
            loglevel_1.default.trace('adding ICE candidate for peer', ev.candidate);
            this.client.sendIceCandidate(ev.candidate, livekit_rtc_1.SignalTarget.PUBLISHER);
        };
        this.subscriber.pc.onicecandidate = (ev) => {
            if (!ev.candidate)
                return;
            this.client.sendIceCandidate(ev.candidate, livekit_rtc_1.SignalTarget.SUBSCRIBER);
        };
        this.publisher.pc.onnegotiationneeded = () => {
            var _a;
            if (((_a = this.publisher) === null || _a === void 0 ? void 0 : _a.pc.iceConnectionState) === 'new') {
                return;
            }
            this.negotiate();
        };
        this.publisher.pc.oniceconnectionstatechange = () => {
            if (!this.publisher) {
                return;
            }
            if (this.publisher.pc.iceConnectionState === 'connected') {
                loglevel_1.default.trace('ICE connected');
                if (!this.iceConnected) {
                    this.iceConnected = true;
                    this.emit(events_2.EngineEvent.Connected);
                }
            }
            else if (this.publisher.pc.iceConnectionState === 'failed') {
                this.iceConnected = false;
                loglevel_1.default.trace('ICE disconnected');
                this.handleDisconnect('peerconnection');
            }
        };
        if (this.useLegacy) {
            this.subscriber.pc.addEventListener('addstream', (ev) => {
                const { stream } = ev;
                stream.getTracks().forEach((t) => {
                    this.emitTrackEvent(t, stream);
                });
            });
        }
        else {
            this.subscriber.pc.ontrack = (ev) => {
                this.emitTrackEvent(ev.track, ev.streams[0], ev.receiver);
            };
        }
        // data channels
        this.lossyDC = this.publisher.pc.createDataChannel(lossyDataChannel, {
            // will drop older packets that arrive
            ordered: true,
            maxRetransmits: 1,
        });
        this.reliableDC = this.publisher.pc.createDataChannel(reliableDataChannel, {
            ordered: true,
        });
        this.lossyDC.onmessage = this.handleDataMessage;
        this.reliableDC.onmessage = this.handleDataMessage;
        // configure signaling client
        this.client.onAnswer = (sd) => __awaiter(this, void 0, void 0, function* () {
            if (!this.publisher) {
                return;
            }
            loglevel_1.default.debug('received server answer', sd.type, this.publisher.pc.signalingState);
            yield this.publisher.setRemoteDescription(sd);
        });
        // add candidate on trickle
        this.client.onTrickle = (candidate, target) => {
            if (!this.publisher || !this.subscriber) {
                return;
            }
            loglevel_1.default.trace('got ICE candidate from peer', candidate, target);
            if (target === livekit_rtc_1.SignalTarget.PUBLISHER) {
                this.publisher.addIceCandidate(candidate);
            }
            else {
                this.subscriber.addIceCandidate(candidate);
            }
        };
        // when server creates an offer for the client
        this.client.onOffer = (sd) => __awaiter(this, void 0, void 0, function* () {
            if (!this.subscriber) {
                return;
            }
            loglevel_1.default.debug('received server offer', sd.type, this.subscriber.pc.signalingState);
            yield this.subscriber.setRemoteDescription(sd);
            // answer the offer
            const answer = yield this.subscriber.pc.createAnswer();
            yield this.subscriber.pc.setLocalDescription(answer);
            this.client.sendAnswer(answer);
        });
        this.client.onParticipantUpdate = (updates) => {
            this.emit(events_2.EngineEvent.ParticipantUpdate, updates);
        };
        this.client.onLocalTrackPublished = (res) => {
            const resolve = this.pendingTrackResolvers[res.cid];
            if (!resolve) {
                loglevel_1.default.error('missing track resolver for ', res.cid);
                return;
            }
            delete this.pendingTrackResolvers[res.cid];
            resolve(res.track);
        };
        this.client.onActiveSpeakersChanged = (speakers) => {
            this.emit(events_2.EngineEvent.SpeakersUpdate, speakers);
        };
        this.client.onClose = () => {
            this.handleDisconnect('signal');
        };
        this.client.onLeave = () => {
            this.close();
            this.emit(events_2.EngineEvent.Disconnected);
        };
    }
    reconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isClosed) {
                return;
            }
            if (!this.url || !this.token) {
                throw new errors_1.ConnectionError('could not reconnect, url or token not saved');
            }
            loglevel_1.default.info('reconnecting to signal connection, attempt', this.reconnectAttempts);
            if (this.reconnectAttempts === 0) {
                this.emit(events_2.EngineEvent.Reconnecting);
            }
            this.reconnectAttempts += 1;
            yield this.client.reconnect(this.url, this.token);
            // trigger publisher reconnect
            if (!this.publisher || !this.subscriber) {
                throw new errors_1.UnexpectedConnectionState('publisher and subscriber connections unset');
            }
            this.publisher.restartingIce = true;
            this.subscriber.restartingIce = true;
            // @ts-ignore
            if (this.publisher.pc.restartIce) {
                // @ts-ignore
                this.publisher.pc.restartIce();
            }
            else {
                yield this.negotiate({ iceRestart: true });
            }
            const startTime = (new Date()).getTime();
            while ((new Date()).getTime() - startTime < 10 * 1000) {
                if (this.iceConnected) {
                    // reconnect success
                    this.emit(events_2.EngineEvent.Reconnected);
                    return;
                }
                yield utils_1.sleep(500);
            }
            // have not reconnected, throw
            throw new errors_1.ConnectionError('could not establish ICE connection');
        });
    }
    negotiate(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.publisher) {
                return;
            }
            const { pc } = this.publisher;
            if (pc.remoteDescription && pc.signalingState === 'have-local-offer') {
                // it's still waiting for the last offer, and it won't be able to create
                // a new offer in this state. We'll reuse the last remote description to
                // get it out of this state
                yield pc.setRemoteDescription(pc.remoteDescription);
            }
            // TODO: what if signaling state changed? will need to queue and retry
            loglevel_1.default.debug('starting to negotiate', pc.signalingState);
            const offer = yield pc.createOffer(options);
            yield pc.setLocalDescription(offer);
            this.client.sendOffer(offer);
        });
    }
}
exports.default = RTCEngine;
//# sourceMappingURL=RTCEngine.js.map